/**
 * Importer une nouvelle version d'un tarif : choisir le fichier Excel, LIRE l'aperçu, publier.
 *
 * DEUX TEMPS, comme l'API. L'aperçu ne modifie RIEN : il dit ce que le fichier contient (feuille et
 * colonnes comprises, erreurs), ce qui changerait par rapport à la version en vigueur (hausses, baisses,
 * nouvelles, disparues) et ce qu'il faut savoir avant de publier. La publication envoie le MÊME fichier,
 * que le serveur reconnaît à son empreinte (sha256), et l'écrit d'un bloc, tout ou rien.
 *
 * Une version publiée ne se modifie plus (« IMMUABLE ») : l'utilisateur le lit avant de confirmer.
 * Les boutons restent collés en bas de la colonne avec les messages, comme l'éditeur de gamme : le
 * refus d'un import s'affiche là où l'on vient de cliquer.
 */
import { useState } from 'react';

import { ApiError, api, type ApercuImportTarif, type TarifDetail, type VersionTarif } from '../api/client';
import {
  dateFrTarif,
  decrireEcarts,
  erreurDateDEffet,
  eurosFr,
  formaterVariation,
  lignesFr,
  ressembleAUnClasseur,
} from '../domaine/tarifs';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { Champ, ErreurFormulaire } from './Formulaire';
import { ZoneDepot } from './ZoneDepot';

export function ImportTarif({
  tarif,
  onPublie,
  onFermer,
}: {
  tarif: TarifDetail;
  /** La version vient d'être publiée : le parent relit le tarif et annonce le résultat. */
  onPublie: (version: VersionTarif) => void;
  onFermer: () => void;
}) {
  const [fichier, setFichier] = useState<File | null>(null);
  const [apercu, setApercu] = useState<ApercuImportTarif | null>(null);
  const [lecture, setLecture] = useState(false);
  const [validFrom, setValidFrom] = useState('');
  const [precision, setPrecision] = useState('');
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const vigueur = tarif.versionEnVigueur;
  const erreurDate = apercu && validFrom !== '' ? erreurDateDEffet(validFrom, vigueur?.validFrom ?? null) : null;

  const choisir = async (f: File | null) => {
    setApercu(null);
    setErreur(null);
    setFichier(f);
    if (!f) return;
    if (!ressembleAUnClasseur(f.name)) {
      setErreur(new Error('Ce fichier n’est pas un classeur Excel (.xlsx). Enregistrez le tarif au format Excel (.xlsx).'));
      return;
    }
    setLecture(true);
    try {
      const lu = await api.apercuTarif(tarif.id, f);
      setApercu(lu);
      // La date lue dans le fichier est une PROPOSITION : l'utilisateur la confirme ou la corrige.
      setValidFrom(lu.validFromDetectee ?? '');
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Lecture du fichier impossible.'));
    } finally {
      setLecture(false);
    }
  };

  const publier = async () => {
    if (!fichier || !apercu) return;
    const defautDate = erreurDateDEffet(validFrom, vigueur?.validFrom ?? null);
    if (defautDate) {
      setErreur(new Error(defautDate));
      return;
    }
    const aVenir = validFrom > aujourdhuiLocal();
    const confirme = window.confirm(
      `Importer « ${apercu.nomFichier} » comme nouvelle version de « ${tarif.label} » ?\n\n` +
        `${lignesFr(apercu.nbLignes)}, en vigueur à partir du ${dateFrTarif(validFrom)}.\n` +
        (aVenir
          ? 'D’ici là, la version précédente reste en vigueur.\n'
          : vigueur
            ? `Elle remplace la version ${vigueur.versionNumber} en vigueur.\n`
            : '') +
        '\nUne version publiée est IMMUABLE : on peut l’archiver, jamais la modifier.',
    );
    if (!confirme) return;

    setEnCours(true);
    setErreur(null);
    try {
      const version = await api.publierTarif(tarif.id, fichier, {
        validFrom,
        sha256: apercu.sha256,
        ...(precision.trim() ? { source: precision.trim() } : {}),
        ...(apercu.feuille ? { feuille: apercu.feuille } : {}),
      });
      onPublie(version);
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Import impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const phrases = apercu?.ecarts ? decrireEcarts(apercu.ecarts) : [];
  const peutPublier = apercu !== null && apercu.publiable && validFrom !== '' && erreurDate === null && !enCours;

  return (
    <div className="import-tarif">
      <div className="message info">
        Déposez ou choisissez le fichier Excel (.xlsx) du fabricant. Vous verrez d’abord ce qu’il contient et ce qui
        changerait : <strong>rien n’est enregistré tant que vous n’avez pas cliqué sur « Publier cette version »</strong>.
      </div>

      <ZoneDepot
        accept=".xlsx"
        desactive={enCours || lecture}
        ariaLabel="Fichier de tarif (.xlsx)"
        libelle="Glissez le fichier Excel (.xlsx) ici, ou"
        nomChoisi={fichier?.name ?? null}
        onFichiers={(fichiers) => void choisir(fichiers[0] ?? null)}
      />

      {lecture && <div className="vide">Lecture du fichier… (quelques secondes pour un grand tarif)</div>}

      {apercu && (
        <div className="import-tarif-apercu">
          <h3>Ce que contient le fichier</h3>
          <p className="aide">
            Feuille « <strong>{apercu.feuille}</strong> », <strong>{lignesFr(apercu.nbLignes)}</strong>. Colonnes
            comprises : référence = « {apercu.entetes.reference} », description = « {apercu.entetes.description} », prix = «{' '}
            {apercu.entetes.prix} »{apercu.entetes.famille ? <>, famille = « {apercu.entetes.famille} »</> : ''}.
          </p>

          {apercu.nbErreurs > 0 && (
            <div className="message erreur">
              <strong>
                {apercu.nbErreurs} erreur{apercu.nbErreurs > 1 ? 's' : ''} dans le fichier : rien ne peut être importé.
              </strong>{' '}
              Corrigez le fichier dans Excel, puis choisissez-le de nouveau.
              <ul className="details-erreur">
                {apercu.erreurs.map((e, i) => (
                  <li key={`${e.ligne ?? 'x'}-${i}`}>{e.ligne === null ? e.message : `Ligne ${e.ligne} : ${e.message}`}</li>
                ))}
                {apercu.nbErreurs > apercu.erreurs.length && <li>… et {apercu.nbErreurs - apercu.erreurs.length} autres.</li>}
              </ul>
            </div>
          )}

          {apercu.avertissements.map((a) => (
            <div key={a} className="message info">
              {a}
            </div>
          ))}
          {apercu.alertes.map((a) => (
            <div key={a} className="message avertissement">
              {a}
            </div>
          ))}

          {apercu.nbErreurs > 0 ? null : apercu.ecarts === null ? (
            <p className="aide">C’est le premier import de ce tarif : il n’y a rien à comparer.</p>
          ) : (
            <>
              <h3>
                Ce qui changerait par rapport à la version {apercu.versionEnVigueur?.versionNumber} en vigueur
              </h3>
              {phrases.length === 0 ? (
                <p className="aide">Aucun écart.</p>
              ) : (
                <ul className="import-tarif-ecarts">
                  {phrases.map((p) => (
                    <li key={p.texte} className={p.nature === 'neutre' ? undefined : `ecart-${p.nature}`}>
                      {p.texte}
                    </li>
                  ))}
                </ul>
              )}
              <VariationsDePrix titre="Les plus fortes hausses" lignes={apercu.ecarts.plusFortesHausses} />
              <VariationsDePrix titre="Les plus fortes baisses" lignes={apercu.ecarts.plusFortesBaisses} />
            </>
          )}

          {apercu.publiable && (
            <div className="import-tarif-publication">
              <Champ
                libelle="Date d’effet"
                requis
                aide={
                  apercu.validFromDetectee
                    ? `Lue dans le fichier (« Valable à partir du ${dateFrTarif(apercu.validFromDetectee)} ») : confirmez-la ou corrigez-la.`
                    : 'Aucune date n’a été trouvée dans le fichier : saisissez celle à partir de laquelle ce tarif s’applique.'
                }
              >
                <input
                  type="date"
                  value={validFrom}
                  disabled={enCours}
                  onChange={(e) => {
                    setValidFrom(e.target.value);
                    setErreur(null);
                  }}
                />
                {erreurDate && <div className="message erreur">{erreurDate}</div>}
              </Champ>
              <Champ libelle="Précision (facultatif)">
                <input
                  type="text"
                  placeholder="par exemple : reçu de KAESER le 12/12/2025"
                  maxLength={300}
                  value={precision}
                  disabled={enCours}
                  onChange={(e) => setPrecision(e.target.value)}
                />
              </Champ>
            </div>
          )}
        </div>
      )}

      <div className="editeur-barre">
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />
        <div className="editeur-barre-actions">
          <span className="aide">
            {apercu === null
              ? 'Aucun fichier lu'
              : apercu.nbErreurs > 0
                ? 'Rien ne peut être importé : corrigez le fichier.'
                : !apercu.publiable
                  ? 'Ce fichier ne peut pas être publié : il est déjà importé.'
                  : `${lignesFr(apercu.nbLignes)} prête${apercu.nbLignes > 1 ? 's' : ''}`}
          </span>
          <div className="saisie-ligne">
            <button type="button" className="secondaire" disabled={enCours} onClick={onFermer}>
              {apercu ? 'Annuler l’import' : 'Fermer'}
            </button>
            <button type="button" disabled={!peutPublier} onClick={() => void publier()}>
              {enCours ? 'Import en cours…' : 'Publier cette version'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VariationsDePrix({
  titre,
  lignes,
}: {
  titre: string;
  lignes: readonly { reference: string; description: string; ancien: number; nouveau: number; variation: number | null }[];
}) {
  if (lignes.length === 0) return null;
  return (
    <div className="tableau-defilant">
      <table className="tableau-ecarts">
        <caption>{titre}</caption>
        <thead>
          <tr>
            <th>Référence</th>
            <th>Désignation</th>
            <th>Avant</th>
            <th>Après</th>
            <th>Variation</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.reference}>
              <td>
                <code>{l.reference}</code>
              </td>
              <td>{l.description}</td>
              <td>{eurosFr(l.ancien)}</td>
              <td>{eurosFr(l.nouveau)}</td>
              <td>{formaterVariation(l.variation)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
